import {
  PunksFilterTuple,
  emptyPunksFilter,
} from '@/services/punksmarket-v1.service';
import { idsToMask } from '@/utils/punksmarket-criteria-builder.util';
import {
  PunksmarketTraitIndexEntry,
  listPunksmarketTraitIndexEntries,
} from '@/utils/punksmarket-trait-catalog';

/** UI filter keys → PunksMarket trait categories (on-chain catalog). */
const FILTER_KEY_CATEGORIES: Record<string, string[]> = {
  sex: ['Type'],
  type: ['Type'],
  hair: ['Hairstyle'],
  ears: ['Ear'],
  eyes: ['Eyewear', 'Eye Makeup'],
  emotion: ['Mouth'],
  mouth: ['Mouth Accessories'],
  beard: ['Facial Hair'],
  face: ['Skin Markings'],
  nose: ['Nose'],
  lips: ['Lip Makeup'],
  neck: ['Neck'],
  cheeks: ['Skin Markings'],
  teeth: ['Mouth'],
};

/** Sex / type filter values → on-chain Type trait name slugs. */
const SEX_TRAIT_SLUGS: Record<string, string[]> = {
  male: ['male1', 'male2', 'male3', 'male4'],
  guy: ['male1', 'male2', 'male3', 'male4'],
  female: ['female1', 'female2', 'female3', 'female4'],
  girl: ['female1', 'female2', 'female3', 'female4'],
  fem: ['female1', 'female2', 'female3', 'female4'],
  zombie: ['zombiemale'],
  ape: ['apemale'],
  alien: ['alienmale'],
};

const IGNORED_FILTER_KEYS = new Set([
  'traitcount',
  'specialtrait',
  'skintone',
  'owner',
]);

export function normalizeFilterValueSlug(value: string): string {
  return value.trim().toLowerCase().replace(/-/g, '');
}

function lookupTraitIds(
  entries: PunksmarketTraitIndexEntry[],
  filterKey: string,
  filterValue: string,
): number[] {
  const key = filterKey.toLowerCase();
  const value = filterValue.toLowerCase();

  if (key === 'sex' || key === 'type') {
    const slugs = SEX_TRAIT_SLUGS[value];
    if (!slugs?.length) {
      return [];
    }
    return entries
      .filter((entry) => entry.category === 'Type' && slugs.includes(entry.slug))
      .map((entry) => entry.id);
  }

  const categories = FILTER_KEY_CATEGORIES[key];
  if (!categories?.length) {
    return [];
  }

  const normalizedSlug = normalizeFilterValueSlug(value);
  return entries
    .filter(
      (entry) =>
        categories.includes(entry.category) && entry.slug === normalizedSlug,
    )
    .map((entry) => entry.id);
}

/**
 * Converts a listings-scraper / grid filter tag (e.g. `hair=mohawk`) into a
 * PunksMarket `Filter` tuple using on-chain trait ids (0..110).
 */
export function buildPunksmarketFilterFromTag(
  filterTag: string,
  entries: PunksmarketTraitIndexEntry[] = listPunksmarketTraitIndexEntries(),
): PunksFilterTuple {
  const trimmed = filterTag?.trim();
  if (!trimmed || !entries.length) {
    return emptyPunksFilter();
  }

  const params = new URLSearchParams(trimmed.replace(/^\?/, ''));

  const dimensionGroups: number[][] = [];
  params.forEach((value, key) => {
    const normalizedKey = key.toLowerCase();
    if (IGNORED_FILTER_KEYS.has(normalizedKey)) {
      return;
    }
    const ids = lookupTraitIds(entries, normalizedKey, value);
    if (ids.length) {
      dimensionGroups.push(ids);
    }
  });

  if (!dimensionGroups.length) {
    return emptyPunksFilter();
  }

  const requiredIds: number[] = [];
  const anyOfIds: number[] = [];

  for (const group of dimensionGroups) {
    if (group.length === 1) {
      requiredIds.push(group[0]!);
    } else {
      anyOfIds.push(...group);
    }
  }

  return {
    requiredTraitMask: idsToMask(requiredIds),
    forbiddenTraitMask: 0n,
    anyOfTraitMask: idsToMask(anyOfIds),
    requiredColorMask: 0n,
    forbiddenColorMask: 0n,
    anyOfColorMask: 0n,
    minPixelCount: 0,
    maxPixelCount: 0,
    minColorCount: 0,
    maxColorCount: 0,
  };
}

export function isPunksmarketFilterEmpty(filter: PunksFilterTuple): boolean {
  return (
    filter.requiredTraitMask === 0n &&
    filter.forbiddenTraitMask === 0n &&
    filter.anyOfTraitMask === 0n &&
    filter.requiredColorMask === 0n &&
    filter.forbiddenColorMask === 0n &&
    filter.anyOfColorMask === 0n &&
    filter.minPixelCount <= 0 &&
    filter.maxPixelCount <= 0 &&
    filter.minColorCount <= 0 &&
    filter.maxColorCount <= 0
  );
}
